import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { handicapLabel, eventHandicap } from '../utils/handicap';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, RefreshCw, Trophy, Users, Flag, Clock, CheckCircle,
  AlertTriangle, ThumbsUp, ThumbsDown, Target, Award, MapPin,
  ChevronDown, ChevronUp, Share2, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { GameSettlements } from './Settlements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlayerGameView = ({ game, onBack, currentUserId }) => {
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showLeaderboard, setShowLeaderboard] = useState(true);
  const [showScorecard, setShowScorecard] = useState(true);
  const [showContests, setShowContests] = useState(false);
  const [contestingHole, setContestingHole] = useState(null);
  const [contestReason, setContestReason] = useState('');
  const [proposedScore, setProposedScore] = useState('');
  const [submittingContest, setSubmittingContest] = useState(false);
  const [votingContest, setVotingContest] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  const gameType = game.game_type;
  const eventId = game.event_id;

  const fetchGameData = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      
      const [gameRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/games/${gameType}/${eventId}/player-view`),
        axios.get(`${API}/games/${gameType}/${eventId}/leaderboard`)
      ]);
      
      setGameData(gameRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      
      if (showToast) toast.success('Game updated');
    } catch (error) {
      console.error('Error fetching game:', error);
      if (showToast) toast.error('Failed to refresh game');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [gameType, eventId]);

  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchGameData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchGameData]);

  const handleCreateContest = async (playerId, holeNumber, currentScore) => {
    if (!proposedScore && !contestReason.trim()) {
      toast.error('Please provide a reason or proposed score');
      return;
    }

    setSubmittingContest(true);
    try {
      await axios.post(`${API}/games/${gameType}/${eventId}/contests`, {
        hole_number: holeNumber,
        player_id: playerId,
        reason: contestReason.trim() || null,
        proposed_score: proposedScore ? parseInt(proposedScore) : null
      });
      
      toast.success('Contest submitted - awaiting votes');
      setContestingHole(null);
      setContestReason('');
      setProposedScore('');
      fetchGameData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contest');
    } finally {
      setSubmittingContest(false);
    }
  };

  const handleVote = async (contestId, vote) => {
    setVotingContest(contestId);
    try {
      const response = await axios.post(
        `${API}/games/${gameType}/${eventId}/contests/${contestId}/vote`,
        { vote }
      );
      
      if (response.data.status === 'approved') {
        toast.success('Contest approved - score updated!');
      } else if (response.data.status === 'rejected') {
        toast.info('Contest rejected');
      } else {
        toast.success(response.data.message);
      }
      
      fetchGameData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit vote');
    } finally {
      setVotingContest(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner w-8 h-8 border-green-600 border-t-transparent" />
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <p className="text-stone-600">Unable to load game data</p>
        <Button onClick={onBack} className="mt-4">Go Back</Button>
      </div>
    );
  }

  const holePars = gameData.hole_pars || [];
  const holeHandicaps = gameData.hole_handicaps || [];
  const players = gameData.players || [];
  const scorecards = gameData.scorecards || [];
  const contests = gameData.contests || [];
  const pendingContests = contests.filter(c => c.status === 'pending');
  const results = gameData.results;
  const isCompleted = gameData.status === 'completed';
  const isSkins = gameType === 'skins';

  // Build score map
  const playerScoreMap = {};
  scorecards.forEach(sc => {
    playerScoreMap[sc.player_id] = sc.hole_scores || [];
  });

  // Get hole numbers based on starting nine
  const startingHole = gameData.starting_nine === 'back' ? 10 : 1;
  const numHoles = gameData.holes || 18;
  const holeNumbers = Array.from({ length: numHoles }, (_, i) => startingHole + i);

  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-stone-900">{gameData.name}</h1>
          <div className="flex items-center gap-2 text-sm text-stone-500">
            {isSkins ? <Target className="w-4 h-4" /> : <Award className="w-4 h-4" />}
            <span>{isSkins ? 'Skins' : 'Stroke Play'}</span>
            {gameData.course_name && (
              <>
                <span className="text-stone-300">•</span>
                <MapPin className="w-3.5 h-3.5" />
                <span>{gameData.course_name}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'bg-green-600 hover:bg-green-700' : ''}
            data-testid="auto-refresh-toggle"
          >
            <Clock className="w-4 h-4 mr-1" />
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchGameData(true)}
            disabled={refreshing}
            data-testid="manual-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`p-3 rounded-xl flex items-center gap-3 ${
        isCompleted ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'
      }`}>
        {isCompleted ? (
          <>
            <CheckCircle className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">Game Complete</span>
          </>
        ) : (
          <>
            <Clock className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">Game In Progress</span>
            <span className="text-sm text-green-600 ml-auto">Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}</span>
          </>
        )}
      </div>

      {/* Pending Contests Alert */}
      {pendingContests.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-3">
            <button
              onClick={() => setShowContests(!showContests)}
              className="w-full flex items-center justify-between"
              data-testid="toggle-contests-btn"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-amber-800">
                  {pendingContests.length} Score Contest{pendingContests.length > 1 ? 's' : ''} Pending
                </span>
              </div>
              {showContests ? <ChevronUp className="w-5 h-5 text-amber-600" /> : <ChevronDown className="w-5 h-5 text-amber-600" />}
            </button>
            
            {showContests && (
              <div className="mt-3 space-y-2">
                {pendingContests.map(contest => {
                  const player = players.find(p => p.player_id === contest.player_id);
                  const hasVoted = contest.votes && contest.votes[currentUserId];
                  const voteCount = Object.keys(contest.votes || {}).length;
                  
                  return (
                    <div key={contest.contest_id} className="p-3 bg-white rounded-lg border border-amber-200">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-stone-900">
                            Hole {contest.hole_number}: {player?.name || 'Unknown'}
                          </p>
                          <p className="text-sm text-stone-600">
                            Current: {contest.current_score ?? '—'} 
                            {contest.proposed_score && ` → Proposed: ${contest.proposed_score}`}
                          </p>
                          {contest.reason && (
                            <p className="text-xs text-stone-500 mt-1 italic">"{contest.reason}"</p>
                          )}
                          <p className="text-xs text-amber-600 mt-1">
                            {voteCount}/{players.filter(p => p.user_id).length} votes
                          </p>
                        </div>
                        
                        {!hasVoted ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => handleVote(contest.contest_id, 'reject')}
                              disabled={votingContest === contest.contest_id}
                              data-testid={`reject-contest-${contest.contest_id}`}
                            >
                              <ThumbsDown className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleVote(contest.contest_id, 'approve')}
                              disabled={votingContest === contest.contest_id}
                              data-testid={`approve-contest-${contest.contest_id}`}
                            >
                              <ThumbsUp className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            hasVoted === 'approve' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            You voted: {hasVoted}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Leaderboard */}
      <Card>
        <CardHeader className="pb-2">
          <button
            onClick={() => setShowLeaderboard(!showLeaderboard)}
            className="w-full flex items-center justify-between"
            data-testid="toggle-leaderboard-btn"
          >
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Leaderboard
            </CardTitle>
            {showLeaderboard ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </CardHeader>
        
        {showLeaderboard && (
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-2 px-2 font-medium text-stone-500">Pos</th>
                    <th className="text-left py-2 px-2 font-medium text-stone-500">Player</th>
                    <th className="text-center py-2 px-2 font-medium text-stone-500">Thru</th>
                    <th className="text-center py-2 px-2 font-medium text-stone-500">Gross</th>
                    {gameData.use_handicaps && (
                      <th className="text-center py-2 px-2 font-medium text-stone-500">Net</th>
                    )}
                    <th className="text-center py-2 px-2 font-medium text-stone-500">To Par</th>
                    {isSkins && results && (
                      <th className="text-center py-2 px-2 font-medium text-stone-500">Skins</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry, idx) => {
                    const isCurrentUser = players.find(p => p.player_id === entry.player_id)?.user_id === currentUserId;
                    return (
                      <tr 
                        key={entry.player_id}
                        className={`border-b border-stone-100 ${isCurrentUser ? 'bg-green-50' : ''}`}
                      >
                        <td className="py-2 px-2">
                          {entry.position === 1 && entry.holes_played > 0 ? (
                            <span className="text-amber-500 font-bold">1st</span>
                          ) : entry.position === 2 && entry.holes_played > 0 ? (
                            <span className="text-stone-400 font-bold">2nd</span>
                          ) : entry.position === 3 && entry.holes_played > 0 ? (
                            <span className="text-amber-700 font-bold">3rd</span>
                          ) : (
                            <span data-testid={`game-player-rank-${entry.player_id}`} className="text-stone-500">{entry.position ?? 'Unranked'}</span>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          <span className={`font-medium ${isCurrentUser ? 'text-green-700' : 'text-stone-900'}`}>
                            {entry.name}
                            {isCurrentUser && <span className="text-xs ml-1">(You)</span>}
                          </span>
                          <span data-testid={`game-player-handicap-${entry.player_id}`} className="text-xs text-stone-400 ml-1">({handicapLabel(entry.handicap)})</span>
                        </td>
                        <td className="py-2 px-2 text-center text-stone-600">{entry.thru}</td>
                        <td className="py-2 px-2 text-center font-mono font-bold">{entry.gross_score || '—'}</td>
                        {gameData.use_handicaps && (
                          <td data-testid={`game-player-net-${entry.player_id}`} className="py-2 px-2 text-center font-mono font-bold text-blue-600">{entry.net_score ?? 'N/A'}</td>
                        )}
                        <td className="py-2 px-2 text-center">
                          <span className={`font-mono font-bold ${
                            entry.to_par < 0 ? 'text-red-600' : entry.to_par > 0 ? 'text-stone-600' : 'text-green-600'
                          }`}>
                            {entry.to_par > 0 ? `+${entry.to_par}` : entry.to_par === 0 ? 'E' : entry.to_par}
                          </span>
                        </td>
                        {isSkins && results && (
                          <td className="py-2 px-2 text-center">
                            <span className="font-bold text-green-600">{entry.skins_won || 0}</span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Course & Scorecard */}
      <Card>
        <CardHeader className="pb-2">
          <button
            onClick={() => setShowScorecard(!showScorecard)}
            className="w-full flex items-center justify-between"
            data-testid="toggle-scorecard-btn"
          >
            <CardTitle className="text-lg flex items-center gap-2">
              <Flag className="w-5 h-5 text-green-600" />
              Scorecard
            </CardTitle>
            {showScorecard ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </CardHeader>
        
        {showScorecard && (
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  {/* Hole Numbers */}
                  <tr className="bg-stone-800 text-white">
                    <th className="py-2 px-2 text-left font-medium sticky left-0 bg-stone-800 z-10">Hole</th>
                    {holeNumbers.map(hole => (
                      <th key={hole} className="py-2 px-2 text-center font-medium min-w-[36px]">{hole}</th>
                    ))}
                    <th className="py-2 px-2 text-center font-medium bg-stone-700">Tot</th>
                  </tr>
                  
                  {/* Par Row */}
                  <tr className="bg-stone-100">
                    <td className="py-1.5 px-2 font-medium text-stone-600 sticky left-0 bg-stone-100 z-10">Par</td>
                    {holeNumbers.map((_, idx) => (
                      <td key={idx} className="py-1.5 px-2 text-center text-stone-600">{holePars[idx] || 4}</td>
                    ))}
                    <td className="py-1.5 px-2 text-center font-bold text-stone-700 bg-stone-200">
                      {holePars.reduce((a, b) => a + b, 0) || 72}
                    </td>
                  </tr>
                  
                  {/* Handicap Row */}
                  {holeHandicaps.length > 0 && (
                    <tr className="bg-blue-50">
                      <td className="py-1.5 px-2 font-medium text-blue-600 sticky left-0 bg-blue-50 z-10">HCP</td>
                      {holeNumbers.map((_, idx) => (
                        <td key={idx} className="py-1.5 px-2 text-center text-blue-600">{holeHandicaps[idx] || ''}</td>
                      ))}
                      <td className="py-1.5 px-2 text-center bg-blue-100"></td>
                    </tr>
                  )}
                </thead>
                
                <tbody>
                  {players.map(player => {
                    const scores = playerScoreMap[player.player_id] || [];
                    const total = scores.reduce((sum, s) => sum + (s || 0), 0);
                    const isCurrentUser = player.user_id === currentUserId;
                    
                    return (
                      <tr 
                        key={player.player_id}
                        className={`border-b border-stone-100 ${isCurrentUser ? 'bg-green-50' : ''}`}
                      >
                        <td className={`py-2 px-2 font-medium sticky left-0 z-10 ${isCurrentUser ? 'bg-green-50 text-green-800' : 'bg-white'}`}>
                          <div className="truncate max-w-[100px]" title={player.name}>
                            {player.name}
                            <span data-testid={`game-scorecard-handicap-${player.player_id}`} className="text-stone-400 font-normal ml-1">({handicapLabel(eventHandicap(player))})</span>
                          </div>
                        </td>
                        {holeNumbers.map((hole, idx) => {
                          const score = scores[idx];
                          const par = holePars[idx] || 4;
                          const diff = score ? score - par : null;
                          
                          // Check if there's a pending contest for this hole/player
                          const hasContest = pendingContests.some(
                            c => c.hole_number === hole && c.player_id === player.player_id
                          );
                          
                          let bgColor = '';
                          if (score !== null && score !== undefined) {
                            if (diff <= -2) bgColor = 'bg-yellow-300'; // Eagle or better
                            else if (diff === -1) bgColor = 'bg-red-400 text-white'; // Birdie
                            else if (diff === 0) bgColor = 'bg-green-500 text-white'; // Par
                            else if (diff === 1) bgColor = 'bg-stone-300'; // Bogey
                            else if (diff >= 2) bgColor = 'bg-stone-500 text-white'; // Double+
                          }
                          
                          return (
                            <td 
                              key={idx} 
                              className={`py-2 px-1 text-center relative ${bgColor} ${hasContest ? 'ring-2 ring-amber-500' : ''}`}
                              onClick={() => {
                                if (score !== null && score !== undefined && !gameData.is_creator && !hasContest) {
                                  setContestingHole({ hole, playerId: player.player_id, playerName: player.name, currentScore: score });
                                }
                              }}
                            >
                              <span className={`font-mono font-bold ${!score ? 'text-stone-300' : ''}`}>
                                {score ?? '—'}
                              </span>
                              {hasContest && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                              )}
                            </td>
                          );
                        })}
                        <td className={`py-2 px-2 text-center font-mono font-bold ${isCurrentUser ? 'bg-green-100' : 'bg-stone-100'}`}>
                          {total || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-4 text-xs">
              <div className="flex items-center gap-1"><span className="w-4 h-4 bg-yellow-300 rounded" /> Eagle+</div>
              <div className="flex items-center gap-1"><span className="w-4 h-4 bg-red-400 rounded" /> Birdie</div>
              <div className="flex items-center gap-1"><span className="w-4 h-4 bg-green-500 rounded" /> Par</div>
              <div className="flex items-center gap-1"><span className="w-4 h-4 bg-stone-300 rounded" /> Bogey</div>
              <div className="flex items-center gap-1"><span className="w-4 h-4 bg-stone-500 rounded" /> Double+</div>
              <div className="flex items-center gap-1 ml-auto text-stone-500">
                <AlertTriangle className="w-3 h-3 text-amber-500" />
                Tap a score to contest
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Contest Modal */}
      {contestingHole && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm animate-fade-in">
            <CardHeader>
              <CardTitle className="text-lg">Contest Score</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="text-sm text-stone-600">
                  <strong>Hole {contestingHole.hole}</strong> - {contestingHole.playerName}
                </p>
                <p className="text-2xl font-bold text-stone-900 mt-1">
                  Current Score: {contestingHole.currentScore}
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-stone-700">Proposed Score (optional)</label>
                <Input
                  type="number"
                  min="1"
                  max="15"
                  value={proposedScore}
                  onChange={(e) => setProposedScore(e.target.value)}
                  placeholder="Enter correct score"
                  data-testid="proposed-score-input"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-stone-700">Reason (optional)</label>
                <Input
                  value={contestReason}
                  onChange={(e) => setContestReason(e.target.value)}
                  placeholder="e.g., Missed stroke counted"
                  data-testid="contest-reason-input"
                />
              </div>
              
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                All players must unanimously approve for the score to change.
              </p>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setContestingHole(null);
                    setContestReason('');
                    setProposedScore('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={() => handleCreateContest(contestingHole.playerId, contestingHole.hole, contestingHole.currentScore)}
                  disabled={submittingContest}
                  data-testid="submit-contest-btn"
                >
                  {submittingContest ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Submit Contest'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Final Results (if completed) */}
      {isCompleted && results && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Final Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isSkins && results.hole_results && (
              <div className="space-y-3">
                <h4 className="font-medium text-stone-700">Skins Won by Hole</h4>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {results.hole_results.map((hr, idx) => {
                    const winner = players.find(p => p.player_id === hr.winner_id);
                    return (
                      <div key={idx} className={`p-2 rounded-lg text-center ${hr.winner_id ? 'bg-green-100' : 'bg-stone-100'}`}>
                        <div className="text-xs text-stone-500">Hole {hr.hole}</div>
                        <div className="font-bold text-stone-900">{hr.skins_won || 0}</div>
                        <div className="text-xs text-green-700 truncate">{winner?.name || (hr.carryover ? 'Carryover' : '—')}</div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="pt-3 border-t border-blue-200">
                  <h4 className="font-medium text-stone-700 mb-2">Total Skins</h4>
                  {Object.entries(results.player_skins || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([playerId, skins]) => {
                      const player = players.find(p => p.player_id === playerId);
                      return (
                        <div key={playerId} className="flex items-center justify-between py-1">
                          <span className="font-medium">{player?.name || 'Unknown'}</span>
                          <span className="font-bold text-green-600">{skins} skin{skins !== 1 ? 's' : ''}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            
            {!isSkins && (results.gross_standings || results.standings) && (
              <div className="space-y-2">
                {(gameData.use_handicaps ? [...(results.net_standings || []), ...(results.gross_standings || []).filter(s => !s.net_eligible)] : results.gross_standings || results.standings).map((standing, idx) => {
                  const player = players.find(p => p.player_id === standing.player_id);
                  const rank = gameData.use_handicaps ? standing.net_rank : idx + 1;
                  return (
                    <div key={standing.player_id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <div className="flex items-center gap-3">
                        <span data-testid={`game-final-rank-${standing.player_id}`} className={`font-bold ${rank === 1 ? 'text-amber-500' : 'text-stone-500'}`}>
                          {rank ?? 'Unranked'}
                        </span>
                        <span className="font-medium">{player?.name || 'Unknown'}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{standing.gross_score}</div>
                        {gameData.use_handicaps && (
                          <div data-testid={`game-final-net-${standing.player_id}`} className="text-xs text-blue-600">Net: {standing.net_score ?? 'N/A'}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Share Button */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-blue-200">
              <Button variant="outline" className="flex-1" data-testid="share-results-btn">
                <Share2 className="w-4 h-4 mr-2" />
                Share Results
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settlements Section - Show for completed games with entry fees */}
      {isCompleted && gameData.entry_fee > 0 && (
        <GameSettlements 
          gameType={gameType}
          eventId={eventId}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
};

export default PlayerGameView;
